import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export default function ReaderModal({ pages, title, onClose }) {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") setIdx((i) => Math.max(0, i - 1));
      if (e.key === "ArrowRight") setIdx((i) => Math.min(pages.length - 1, i + 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pages.length, onClose]);

  if (!pages?.length) return null;

  return (
    <AnimatePresence>
      <motion.div
        data-testid="reader-modal"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[70] bg-void/95 backdrop-blur-md flex flex-col items-center justify-center p-4"
        onClick={onClose}
      >
        <div className="flex items-center justify-between w-full max-w-3xl mb-4" onClick={(e) => e.stopPropagation()}>
          <p className="font-marker text-bloodglow text-sm -rotate-1">free taste — {title}</p>
          <button data-testid="reader-close-button" onClick={onClose} className="text-paper/60 hover:text-crimson transition-colors" aria-label="Close preview">
            <X size={26} />
          </button>
        </div>
        <div className="relative flex items-center justify-center w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
          <button
            data-testid="reader-prev"
            onClick={() => setIdx((i) => Math.max(0, i - 1))}
            disabled={idx === 0}
            className="absolute left-0 z-10 p-3 bg-void/85 border border-paper/20 text-paper/70 hover:text-crimson hover:border-crimson disabled:opacity-30 transition-colors"
            aria-label="Previous page"
          >
            <ChevronLeft size={22} />
          </button>
          <motion.img
            key={pages[idx]}
            src={pages[idx]}
            alt={`${title} preview page ${idx + 1}`}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.25 }}
            className="max-h-[75vh] w-auto border-2 border-blooddeep object-contain bg-charcoal"
          />
          <button
            data-testid="reader-next"
            onClick={() => setIdx((i) => Math.min(pages.length - 1, i + 1))}
            disabled={idx === pages.length - 1}
            className="absolute right-0 z-10 p-3 bg-void/85 border border-paper/20 text-paper/70 hover:text-crimson hover:border-crimson disabled:opacity-30 transition-colors"
            aria-label="Next page"
          >
            <ChevronRight size={22} />
          </button>
        </div>
        <p data-testid="reader-counter" className="font-courier text-xs text-paper/50 mt-4 tracking-widest">
          PAGE {idx + 1} / {pages.length} — THE REST COSTS A SOUL OR TWO
        </p>
      </motion.div>
    </AnimatePresence>
  );
}
